import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, MessageSquare, Sparkles, ExternalLink, RefreshCw } from "lucide-react";
import { useFamily } from "../context/FamilyContext";
import { api, errMsg, streamPost } from "../lib/api";
import { useAutosave } from "../lib/useAutosave";
import { Chips, PageTitle, AiBadge, OriginalWords } from "../components/Bits";

const RELS = ["Neighbor", "Cousin", "Church friend", "Friend", "Relative"];
const LANGS = ["English", "Tagalog", "Taglish", "Spanish"];
const TONES = ["Warm", "Casual", "Formal"];

const smsHref = (phone, body) => {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const p = (phone || "").replace(/[^\d+]/g, "");
  return `sms:${p}${ios ? "&" : "?"}body=${encodeURIComponent(body)}`;
};

export default function Ask() {
  const { fid, isDemo, data, refresh } = useFamily();
  const demoDefaults = { name: "Rosa", phone: "(555) 010-0123", relationship: "Neighbor", language: "Taglish", tone: "Warm" };
  const [f, setF] = useAutosave(`handoff_ask_${fid}`, isDemo ? demoDefaults : { name: "", phone: "", relationship: "", language: "English", tone: "Warm" });
  const [draft, setDraft, clearDraft] = useAutosave(`handoff_ask_draft_${fid}`, { ai: "", text: "", helper_id: null, token: null });
  const [busy, setBusy] = useState(false);
  const outRef = useRef(null);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const link = draft.token ? `${window.location.origin}/join/${draft.token}` : "";
  const full = draft.text ? `${draft.text}\n\n${link}` : "";

  useEffect(() => {
    if (busy) outRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [busy]);

  const write = async () => {
    setBusy(true);
    try {
      const h = await api.post(`/families/${fid}/helpers`, { ...f, tone: f.tone.toLowerCase(), helper_id: draft.helper_id });
      let acc = "";
      setDraft({ ai: "", text: "", helper_id: h.data.helper_id, token: h.data.invite_token });
      await streamPost(`/families/${fid}/ask/draft`, { ...f, tone: f.tone.toLowerCase() }, (d) => {
        acc += d;
        setDraft({ ai: acc, text: acc, helper_id: h.data.helper_id, token: h.data.invite_token });
      });
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Copied. Paste it into your messages.");
    } catch {
      toast.error("Couldn't copy. Press and hold the text to copy it.");
    }
  };

  const ready = f.name.trim() && f.relationship.trim();
  const caregiver = data.family.caregiver_name?.split(" ")[0] || "you";

  return (
    <div className="space-y-6">
      <PageTitle
        eyebrow="The Ask"
        title="Asking for help is brave."
        sub="Many of us feel shy asking. In Filipino culture this is called hiya. We'll help you find the words, and you send it from your own phone."
      />
      <div className="hf-card space-y-6">
        <div>
          <label className="block font-heading text-lg font-semibold">Who would you like to ask?</label>
          <input data-testid="ask-name-input" className="hf-input mt-2" placeholder="Their first name" value={f.name} onChange={set("name")} />
        </div>
        <div>
          <label className="block font-heading text-lg font-semibold">Their phone number</label>
          <input data-testid="ask-phone-input" type="tel" className="hf-input mt-2" placeholder="Optional, for opening Messages" value={f.phone} onChange={set("phone")} />
        </div>
        <div>
          <label className="block font-heading text-lg font-semibold">How do you know them?</label>
          <div className="mt-3"><Chips options={RELS} value={f.relationship} onChange={set("relationship")} testid="ask-rel" /></div>
          <input data-testid="ask-relationship-input" className="hf-input mt-3" placeholder="Or type it, like Tita or coworker" value={RELS.includes(f.relationship) ? "" : f.relationship} onChange={set("relationship")} />
        </div>
        <div>
          <label className="block font-heading text-lg font-semibold">Language</label>
          <div className="mt-3"><Chips options={LANGS} value={f.language} onChange={set("language")} testid="ask-lang" /></div>
        </div>
        <div>
          <label className="block font-heading text-lg font-semibold">Tone</label>
          <div className="mt-3"><Chips options={TONES} value={f.tone} onChange={set("tone")} testid="ask-tone" /></div>
        </div>
        <button data-testid="ask-write-button" disabled={!ready || busy} onClick={write} className="hf-btn hf-btn-primary w-full">
          {draft.ai ? <RefreshCw className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
          {busy ? "Writing..." : draft.ai ? "Write it another way" : "Write my text"}
        </button>
      </div>

      {(draft.ai || busy) && (
        <div ref={outRef} className="space-y-4 hf-rise">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl font-semibold">Your text to {f.name}</h2>
            <AiBadge>Edit anything</AiBadge>
          </div>
          <div className="hf-bubble-in rounded-[24px] rounded-br-md bg-hf-green p-1">
            <textarea
              data-testid="ask-draft-textarea"
              className="min-h-[200px] w-full resize-none rounded-[1.6rem] rounded-br-md bg-hf-green p-5 text-lg leading-relaxed text-hf-cream outline-none placeholder:text-hf-cream/60"
              value={draft.text}
              placeholder="Writing..."
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
            />
            {link && <p data-testid="ask-invite-link" className="break-all px-5 pb-4 text-base text-hf-sun underline">{link}</p>}
          </div>
          <OriginalWords label="What you told us" testid="ask-original-words">
            {`Ask ${f.name}, your ${f.relationship.toLowerCase()}, in ${f.language}, ${f.tone.toLowerCase()} tone.`}
          </OriginalWords>
          {draft.text !== draft.ai && draft.ai && (
            <OriginalWords label="Original AI draft" testid="ask-ai-original">{draft.ai}</OriginalWords>
          )}
          {!busy && draft.text && (
            <div className="grid gap-3 sm:grid-cols-2">
              <a data-testid="ask-open-messages" href={smsHref(f.phone, full)} className="hf-btn hf-btn-coral">
                <MessageSquare className="h-5 w-5" /> Open in Messages
              </a>
              <button data-testid="ask-copy-button" onClick={copy} className="hf-btn hf-btn-ghost">
                <Copy className="h-5 w-5" /> Copy text
              </button>
              <a data-testid="ask-preview-invite" href={link} target="_blank" rel="noreferrer" className="inline-flex min-h-[48px] items-center justify-center gap-2 text-base font-semibold text-hf-green underline sm:col-span-2">
                <ExternalLink className="h-4 w-4" /> Preview what {f.name} will see
              </a>
              <button data-testid="ask-new-person" onClick={() => { clearDraft(); setF({ name: "", phone: "", relationship: "", language: f.language, tone: f.tone }); }} className="min-h-[48px] text-base text-hf-muted underline sm:col-span-2">
                Ask someone else
              </button>
            </div>
          )}
          <p className="text-base text-hf-muted">Sent from {caregiver}'s own phone. Handoff never texts anyone for you.</p>
        </div>
      )}
    </div>
  );
}
